import { useState } from 'react';
import { useUsers, useSyncFromLdap } from '@/hooks/useUsers';
import { useRocketChatDMHistory, useSendMessage, RocketChatMessage } from '@/hooks/useMessages';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Users as UsersIcon,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Shield,
  User as UserIcon,
  Mail,
  Clock,
  MessageSquare,
  Send,
  X,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/helpers';
import { useAuth } from '@/context/AuthContext';
import { EmailField } from '@/components/users/EmailField';
import { User, UserRole } from '@/types';

/**
 * Role badge component
 */
function RoleBadge({ role }: { role: UserRole }) {
  const roleConfig: Record<UserRole, { label: string; className: string }> = {
    executive: { label: 'Executive', className: 'bg-purple-100 text-purple-800' },
    pm: { label: 'PM', className: 'bg-blue-100 text-blue-800' },
    developer: { label: 'Developer', className: 'bg-green-100 text-green-800' },
    support: { label: 'Support', className: 'bg-orange-100 text-orange-800' },
    viewer: { label: 'Viewer', className: 'bg-gray-100 text-gray-800' },
  };

  const config = roleConfig[role];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.className}`}>
      <Shield className="h-3 w-3 mr-1" />
      {config.label}
    </span>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center text-xs text-green-600">
      <CheckCircle className="h-3 w-3 mr-1" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center text-xs text-red-600">
      <XCircle className="h-3 w-3 mr-1" />
      Inactive
    </span>
  );
}

/**
 * Message bubble component for Rocket.Chat messages
 */
function MessageBubble({ message, botUsername }: { message: RocketChatMessage; botUsername: string }) {
  const isOutgoing = message.u.username === botUsername;

  return (
    <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 ${
          isOutgoing
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted'
        }`}
      >
        <div className="flex items-center gap-1 mb-1">
          {isOutgoing ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownLeft className="h-3 w-3" />
          )}
          <span className="text-xs opacity-70">
            {message.u.name || message.u.username}
          </span>
        </div>
        <p className="text-sm whitespace-pre-wrap">{message.msg}</p>
        <div className="flex items-center justify-end mt-1 text-xs opacity-70">
          <span>{formatRelativeTime(message.ts)}</span>
        </div>
      </div>
    </div>
  );
}


/**
 * User detail panel with messaging
 */
function UserDetailPanel({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const { user: me } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [messageCount, setMessageCount] = useState(50);

  // Fetch messages directly from Rocket.Chat
  const { data: messages, isLoading: messagesLoading, refetch: refetchMessages } = useRocketChatDMHistory(
    user.rocketchatUsername,
    messageCount
  );
  const sendMessageMutation = useSendMessage();

  // Bot username for determining message direction (the Rocket.Chat bot user)
  const botUsername = 'assistant';

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await sendMessageMutation.mutateAsync({
        userId: user.id,
        content: newMessage,
      });
      setNewMessage('');
      // Refetch messages after sending
      setTimeout(() => refetchMessages(), 500);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="w-full max-w-lg bg-background h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <UserIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">{user.displayName}</h3>
              <p className="text-sm text-muted-foreground">@{user.username}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b bg-muted/50">
          <div className="flex flex-wrap gap-4 text-sm">
            <EmailField userId={user.id} email={user.email} canEdit={me?.role === 'executive'} />
            <RoleBadge role={user.role} />
            <StatusBadge active={user.active} />
          </div>
          {user.rocketchatUsername && (
            <div className="mt-2 text-sm text-muted-foreground">
              Rocket.Chat: @{user.rocketchatUsername}
            </div>
          )}
          {!user.rocketchatUsername && (
            <div className="mt-2 text-sm text-orange-600">
              No Rocket.Chat username - cannot send messages
            </div>
          )}
        </div>

        {/* Messages Header */}
        <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
          <span className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Messages
          </span>
          <select
            value={messageCount}
            onChange={(e) => setMessageCount(parseInt(e.target.value, 10))}
            className="text-sm px-2 py-1 rounded border bg-background"
          >
            <option value="20">Last 20</option>
            <option value="50">Last 50</option>
            <option value="100">Last 100</option>
          </select>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messagesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : !user.rocketchatUsername ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No Rocket.Chat username configured</p>
            </div>
          ) : !messages?.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No messages yet</p>
            </div>
          ) : (
            // Show messages in chronological order (oldest first)
            [...messages].reverse().map((message) => (
              <MessageBubble key={message._id} message={message} botUsername={botUsername} />
            ))
          )}
        </div>

        {/* Message Input */}
        {user.rocketchatUsername && (
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sendMessageMutation.isPending}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendMessageMutation.isPending}
              >
                {sendMessageMutation.isPending ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {sendMessageMutation.isError && (
              <p className="text-xs text-red-500 mt-1">
                Failed to send message. Please try again.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * User card component
 */
function UserCard({ user, onClick }: { user: User; onClick: () => void }) {
  return (
    <Card
      className="hover:border-primary/50 transition-colors cursor-pointer"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <UserIcon className="h-6 w-6 text-primary" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium truncate">{user.displayName}</span>
              <RoleBadge role={user.role} />
              <StatusBadge active={user.active} />
            </div>

            <p className="text-sm text-muted-foreground">@{user.username}</p>

            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {user.email}
              </span>
              {user.lastActiveAt && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last active: {formatRelativeTime(user.lastActiveAt)}
                </span>
              )}
            </div>

            {/* Integration usernames */}
            <div className="flex flex-wrap gap-2 mt-2">
              {user.gitlabUsername && (
                <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                  GitLab: @{user.gitlabUsername}
                </span>
              )}
              {user.rocketchatUsername && (
                <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded">
                  Rocket.Chat: @{user.rocketchatUsername}
                </span>
              )}
            </div>
          </div>

          {/* Message button */}
          <Button
            variant="ghost"
            size="icon"
            className="flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            <MessageSquare className="h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Users() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Queries
  const { data: usersData, isLoading, refetch } = useUsers({
    page,
    limit: 20,
    search: searchQuery || undefined,
    role: roleFilter || undefined,
    sortBy: 'displayName',
    sortOrder: 'asc',
  });

  // Mutations
  const syncLdapMutation = useSyncFromLdap();

  const handleSyncLdap = async () => {
    await syncLdapMutation.mutateAsync();
    refetch();
  };

  // Stats
  const totalUsers = usersData?.pagination?.total || 0;
  const roleStats = usersData?.data?.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">
            Manage users synced from LDAP
          </p>
        </div>
        <Button
          onClick={handleSyncLdap}
          disabled={syncLdapMutation.isPending}
        >
          {syncLdapMutation.isPending ? (
            <LoadingSpinner className="h-4 w-4 mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Sync from LDAP
        </Button>
      </div>

      {/* Sync Result */}
      {syncLdapMutation.isSuccess && syncLdapMutation.data && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-3">
            <p className="text-sm text-green-800">
              LDAP sync complete: {syncLdapMutation.data.created} created, {syncLdapMutation.data.updated} updated, {syncLdapMutation.data.deactivated} deactivated ({syncLdapMutation.data.total} total in group)
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">User Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5 text-primary" />
              <span className="font-medium">{totalUsers}</span>
              <span className="text-muted-foreground">total users</span>
            </div>
            {Object.entries(roleStats).map(([role, count]) => (
              <div key={role} className="flex items-center gap-2 text-muted-foreground">
                <RoleBadge role={role as UserRole} />
                <span>{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value as UserRole | '');
            setPage(1);
          }}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All roles</option>
          <option value="executive">Executive</option>
          <option value="pm">PM</option>
          <option value="developer">Developer</option>
          <option value="support">Support</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>

      {/* User List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !usersData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No users found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || roleFilter
                ? 'Try different filters'
                : 'Sync from LDAP to import users'}
            </p>
            {!searchQuery && !roleFilter && (
              <Button onClick={handleSyncLdap} disabled={syncLdapMutation.isPending}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync from LDAP
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {usersData.data.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              onClick={() => setSelectedUser(user)}
            />
          ))}
        </div>
      )}

      {/* User Detail Panel */}
      {selectedUser && (
        <UserDetailPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* Pagination */}
      {usersData?.pagination?.totalPages && usersData.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {usersData.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= usersData.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
